/**
 * Umami Analytics Integration
 *
 * Provides page view tracking (automatic) and custom event tracking.
 * Gracefully degrades when Umami is not loaded (e.g., local development).
 *
 * The tracker is the copy pinned in packages/umami-tracker, emitted as a
 * fingerprinted asset on this origin (task 370), so no third-party script runs
 * on the dashboard and `script-src` names no analytics host. `no-inline`
 * because a data: URL is not 'self'. Its beacon still goes to
 * gateway.umami.is, which WebStack's connect-src allows.
 */
import umamiTrackerUrl from "@headerhawk/umami-tracker/tracker.js?url&no-inline";

import { isDemoTab, isImpersonationTab } from "@/lib/auth/impersonation";

declare global {
  interface Window {
    umami?: {
      track: (
        eventName: string,
        eventData?: Record<string, string | number>,
      ) => void;
    };
  }
}

/**
 * Initialize Umami analytics by injecting the tracking script.
 * Call once at app startup (in main.tsx).
 *
 * Does nothing if VITE_UMAMI_WEBSITE_ID is not set.
 */
export function initAnalytics(): void {
  const websiteId = import.meta.env.VITE_UMAMI_WEBSITE_ID;

  if (!websiteId) {
    if (import.meta.env.DEV) {
      console.debug("[Analytics] Umami disabled - no website ID configured");
    }
    return;
  }

  // Avoid double-initialization
  if (document.querySelector("script[data-website-id]")) {
    return;
  }

  const script = document.createElement("script");
  script.defer = true;
  script.src = umamiTrackerUrl;
  script.setAttribute("data-website-id", websiteId);
  // The public demo (task 405) sends its own events and nothing else: no
  // pageviews, so the dashboard's traffic never counts a demo visitor.
  if (isDemoTab()) {
    script.setAttribute("data-auto-track", "false");
  }
  script.addEventListener("load", flushDemoEvents);
  document.head.appendChild(script);

  if (import.meta.env.DEV) {
    console.debug("[Analytics] Umami initialized with website ID:", websiteId);
  }
}

/**
 * Track a custom event in Umami.
 *
 * No-op if Umami is not loaded or website ID is not configured.
 * Safe to call anywhere - will not throw if Umami is unavailable.
 *
 * @param eventName - Snake_case event name (e.g., "signup_completed")
 * @param eventData - Optional key-value data (no PII - no emails, names, IDs)
 *
 * @example
 * trackEvent("checkout_started", { tier: "professional" });
 * trackEvent("site_created");
 */
export function trackEvent(
  eventName: string,
  eventData?: Record<string, string | number>,
): void {
  // An operator's view of a member (task 394) never loads Umami; this is the
  // belt to that, since the funnel would count the event as the member's.
  if (isImpersonationTab()) return;
  if (typeof window !== "undefined" && window.umami) {
    window.umami.track(eventName, eventData);
  }
}

/**
 * The public demo's events (task 405): the only ones a demo tab sends, and
 * never sent from any other tab. `demo_open` is the marketing site's, on its
 * links into the demo.
 */
export const DemoEvents = {
  STARTED: "demo_started",
  SECTION: "demo_section",
  SHOP_OPEN: "demo_shop_open",
  SIGNUP_CLICK: "demo_signup_click",
} as const;

export type DemoEvent = (typeof DemoEvents)[keyof typeof DemoEvents];

/** Events sent before the tracker loaded, sent once it has. */
const pendingDemoEvents: [DemoEvent, Record<string, string> | undefined][] = [];

function flushDemoEvents(): void {
  if (typeof window === "undefined" || !window.umami) return;
  for (const [name, data] of pendingDemoEvents.splice(0)) {
    window.umami.track(name, data);
  }
}

/**
 * Send one of the demo's events. A no-op outside a demo tab. Queued until
 * the tracker has loaded, because `demo_started` fires as the dashboard's
 * first page renders, before a deferred script has run.
 */
export function trackDemoEvent(
  eventName: DemoEvent,
  eventData?: Record<string, string>,
): void {
  if (!isDemoTab() || typeof window === "undefined") return;
  if (window.umami) {
    window.umami.track(eventName, eventData);
    return;
  }
  pendingDemoEvents.push([eventName, eventData]);
}

/**
 * Standard event names for consistency across the app.
 * Use these constants instead of string literals.
 */
export const AnalyticsEvents = {
  // Auth flow
  SIGNUP_STARTED: "signup_started",
  SIGNUP_COMPLETED: "signup_completed",

  // The first run (task 512): the URL submitted, and the grade the
  // Overview led with (`{ grade }`, the letter). Before it, onboarding sent
  // `onboarding_step` per step and `tier_selected` on its plan step. Task
  // 515 adds the grade's dialog opened, once per opening (`{ grade }`).
  FIRST_RUN_URL_SUBMITTED: "first_run_url_submitted",
  FIRST_RUN_GRADE_SHOWN: "first_run_grade_shown",
  FIRST_RUN_GRADE_EXPLAINED: "first_run_grade_explained",

  // Site management
  SITE_CREATED: "site_created",

  // Billing flow
  CHECKOUT_STARTED: "checkout_started",
  CHECKOUT_COMPLETED: "checkout_completed",

  // API keys (task 442): never the key, its prefix or its name
  API_KEY_CREATED: "api_key_created",
  API_KEY_REVOKED: "api_key_revoked",
  API_KEY_CHECK_COPIED: "api_key_check_copied",
} as const;
